import { Model } from '@vuex-orm/core';
import { prepareQuestions } from '@/helpers/questions';
import VacancyScript from '@/orm/models/Interview/VacancyScript';
import Vacancy from '@/orm/models/Vacancy/Vacancy';
import InterviewPreset from '@/orm/models/Interview/InterviewPreset';
import Interviewer from '@/orm/models/Interview/Interviewer';
import { wizardSteps } from '@/enums';
import InterviewVideoCategoryVideo from '@/orm/models/Interview/InterviewVideoCategoryVideo';
import InterviewVideoQuestionVideo from '@/orm/models/Interview/InterviewVideoQuestionVideo';
import { response } from '@/orm/functions';
import InterviewPresetCategory from '@/orm/models/Interview/InterviewPresetCategory';
import RouterController from '@/orm/models/Wizard/RouterController';

export default class Wizard extends Model {
  static entity = 'wizard'

  static stateFieldsForSave = [
    'selectedQuestions',
    'vacancyId',
    'presetId',
    'interviewerId',
    'categoryId',
    'index',
    'step',
    'isCompleted',
    'isAiInterviewer',
    'isAiTemplate',
    'prevStep',
  ]

  static routerController = new RouterController({});;

  static vacancy = null;

  static state() {
    return {
      selectedQuestions: [],
      vacancyId: null,
      presetId: null,
      interviewerId: null,
      step: wizardSteps.STORE,
      prevStep: null,
      categoryId: null,
      index: null,
      route: null,
      isCompleted: false,
      fetched: false,
      isAiInterviewer: false,
      isAiTemplate: false,
    };
  }

  static modelState() {
    return this.store().state.entities[this.entity];
  }

  static setVacancyId(vacancyId) {
    this.commit((state) => {
      state.vacancyId = vacancyId;
    });
    return this;
  }

  static setPresetId(presetId) {
    this.commit((state) => {
      state.presetId = presetId;
    });
    return this;
  }

  static setInterviewerId(interviewerId) {
    this.commit((state) => {
      state.interviewerId = interviewerId;
    });
    return this;
  }

  static async changeInterviewer(interviewerId) {
    await this.clearInterviewerData();
    this.setInterviewerId(interviewerId);
  }

  static setWizardStep(step) {
    this.commit((state) => {
      state.step = step;
    });
    return this;
  }

  static setPrevStep(step) {
    this.commit((state) => {
      state.prevStep = step;
    });
    return this;
  }

  static setSelectedQuestions(questions) {
    this.commit((state) => {
      state.selectedQuestions = (questions || []).map((it) => ({
        ...it, id: it.id || `text_question_${it.questionId}`,
      }));
    });
    return this;
  }

  static setCategory(categoryId) {
    this.commit((state) => {
      state.categoryId = categoryId;
    });
    return this;
  }

  static setIsAiInterviewer(isAiInterviewer) {
    this.commit((state) => {
      state.isAiInterviewer = isAiInterviewer;
    });
  }

  static setIsAiTemplate(isAiTemplate) {
    this.commit((state) => {
      state.isAiTemplate = isAiTemplate;
    });
  }

  static setFetched(val) {
    this.commit((state) => {
      state.fetched = val;
    });
  }

  static setIndex(index) {
    this.commit((state) => {
      state.index = index;
    });
    return this;
  }

  static setRoute(route) {
    this.commit((state) => {
      state.route = route;
    });
    return this;
  }

  static setIsCompleted(isCompleted) {
    this.commit((state) => {
      state.isCompleted = isCompleted;
    });
    return this;
  }

  static fetchRoute(step) {
    if (step) {
      this.setRoute(this.routerController.makeRoute({
        step,
        wizardState: this.modelState(),
      }));
    }
  }

  static findCurrentRoute() {
    const { step } = this.modelState();
    return this.routerController.findRoute(step);
  }

  static pushPrevRoute(router, params) {
    this.routerController.wizardState = this.modelState();
    this.routerController.prev(router, params);
  }

  static async pushNextRoute(router, params) {
    this.routerController.wizardState = this.modelState();
    if (!this.routerController.routes) {
      this.routerController.initRoutes();
    }
    const nextRoute = this.routerController.makeNextRoute(params);
    if (nextRoute) {
      const route = this.routerController.routes.find((r) => r.name === nextRoute.name);
      if (route && route.step) {
        this.setWizardStep(route.step);
        await this.saveWizardState();
      }
      router.push(nextRoute);
    }
  }

  static async saveWizardState() {
    const data = {};
    this.stateFieldsForSave.forEach((field) => {
      data[field] = this.modelState()[field];
    });
    await Vacancy.api().changeWizardState(this.modelState().vacancyId, data);
  }

  static async clearWizardState() {
    await Vacancy.api().changeWizardState(this.modelState().vacancyId, {});
  }

  static async restoreState(wizardState) {
    if (wizardState) {
      if (!this.modelState().vacancyId) {
        this.setVacancyId(wizardState.vacancyId);
      }
      if (!this.modelState().selectedQuestions.length) {
        this.setSelectedQuestions(wizardState.selectedQuestions);
      }
      if (!this.modelState().presetId) {
        this.setPresetId(wizardState.presetId);
      }
      if (!this.modelState().interviewerId) {
        this.setInterviewerId(wizardState.interviewerId);
      }
      if (!this.modelState().categoryId) {
        this.setCategory(wizardState.categoryId);
      }
      if (!this.modelState().index) {
        this.setIndex(wizardState.index);
      }
      if (!this.modelState().isCompleted) {
        this.setIsCompleted(wizardState.isCompleted);
      }
      if (!this.modelState().prevStep) {
        this.setPrevStep(wizardState.prevStep);
      }
      this.fetchRoute(wizardState.step);
    }
  }

  static async fetchData(noRestore = false) {
    let promises = [];
    this.setFetched(false);
    if (this.modelState().vacancyId > 0) {
      const vacancy = await this.fetchVacancy();
      Wizard.vacancy = vacancy;
      Wizard.routerController = new RouterController({ vacancy: Wizard.vacancy });
      Wizard.routerController.initRoutes();
      if (!noRestore) {
        await this.restoreState(vacancy.wizardState);
      }
    }
    if (this.modelState().interviewerId > 0 && !this.modelState().isAiTemplate) {
      promises = promises.concat([
        this.fetchCategories(),
        this.fetchQuestions(),
      ]);
    }
    await Promise.all(promises);
    this.setFetched(true);
  }

  static async fetchVacancy() {
    return response(await Vacancy.api().fetchById(this.modelState().vacancyId), null);
  }

  static async fetchPresets() {
    await InterviewPreset.api().fetch();
  }

  static async fetchPresetsCategories() {
    await InterviewPresetCategory.api().fetch();
  }

  static async fetchInterviewers() {
    await Interviewer.api().fetchAll();
  }

  static async fetchScript() {
    const questions = await response(
      await InterviewVideoQuestionVideo.api()
        .fetchAllByPresetIdAndInterviewerId(this.modelState().presetId, this.modelState().interviewerId),
    );
    this.setSelectedQuestions(questions);
  }

  static async fetchCategories() {
    await InterviewVideoCategoryVideo.api().fetchAllByInterviewerId(this.modelState().interviewerId);
  }

  static async fetchQuestions() {
    await InterviewVideoQuestionVideo.api().fetchAllByInterviewerId(this.modelState().interviewerId);
  }

  static async clearInterviewerData() {
    await InterviewVideoCategoryVideo.deleteAll();
    await InterviewVideoQuestionVideo.deleteAll();
  }

  static async submitQuestions() {
    if (this.modelState().selectedQuestions.length > 0) {
      if (this.vacancy.hasAiQuestions) {
        const response = await InterviewVideoQuestionVideo.api().fetchAllByInterviewerId(this.modelState().interviewerId);
        this.setSelectedQuestions([response.response.data.data[0]]);
      }
      await VacancyScript.api().store(this.modelState().vacancyId, {
        interviewer: this.modelState().interviewerId,
        questions: prepareQuestions(this.modelState().selectedQuestions),
      });
    }
  }
}
